import { WebSocketService } from '@/ws/WebSocketService.ts';
import type { InputSource } from '@/types/InputSource.ts';
import { handleRequestSourcesResponseAction } from '@/actions/electron/handlers/HandleRequestSourcesResponseAction.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { requestInputSourcesAction } from '@/actions/electron/RequestInputSourcesAction.ts';
import { handleGlobalErrorAction } from '@/actions/app/HandleGlobalErrorAction.ts';
import { handleRequestSourcesIdListResponseAction } from '@/actions/electron/handlers/HandleRequestSourcesIdListResponseAction.ts';
import { handleRequestDesktopAppVersionResponseAction } from '@/actions/electron/handlers/HandleRequestDesktopAppVersionResponseAction.ts';
import { requestDesktopAppVersionAction } from '@/actions/electron/RequestDesktopAppVersionAction.ts';
import { registerSettingsStoreWatcher, useSettingsStore } from '@/stores/SettingsStore.ts';
import { handleHotkeyPressAction } from '@/actions/app/HandleHotkeyPressAction.ts';
import { refreshTokenAction } from '@/actions/auth/RefreshTokenAction.ts';
import { registerRouterSyncHook } from '@/router.ts';
import { eventEmitter, type StreamFinishedPayload } from '@/lib/eventEmitter.ts';
import { logger } from '@/lib/logger.ts';
import { registerPromptStoreWatcher } from '@/stores/PromptStore.ts';
import { registerClientStoreWatcher } from '@/stores/ClientStore.ts';
import { registerSourceStoreWatcher } from '@/stores/SourceStore.ts';
import { VariableService } from '@/services/VariableService.ts';
import { retrieveUserInfoAction } from '@/actions/auth/RetrieveUserInfoAction.ts';
import { DefaultHotkeys } from '@/constants/DefaultHotkeys.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { registerHotkeysAction } from '@/actions/electron/RegisterHotkeysAction.ts';
import { Hotkey } from '@/types/Hotkey.ts';
import { setTaskbarIconVisibilityAction } from '@/actions/electron/SetTaskbarIconVisibilityAction.ts';
import { setAppSizeAction } from '@/actions/app/SetAppSizeAction.ts';

export const bootstrapDesktopClient = () => {
    runStoreMigrations();

    const sessionStore = useSessionStore();
    sessionStore.isInsideWebView = false;

    const settingsStore = useSettingsStore();
    logger.level = settingsStore.loggingLevel;

    window.electronApi.onShortcutPress((hotkey: Hotkey) => {
        void handleHotkeyPressAction(hotkey);
    });

    window.electronApi.onGlobalError((errorMessage: string) => {
        void handleGlobalErrorAction(errorMessage);
    });

    window.electronApi.onRequestSourcesResponse((sources: InputSource[]) => {
        void handleRequestSourcesResponseAction(sources);
    });

    window.electronApi.onRequestSourcesIdListResponse((sourceIds) => {
        void handleRequestSourcesIdListResponseAction(sourceIds);
    });

    window.electronApi.onRequestDesktopAppVersionResponse((version: string) => {
        void handleRequestDesktopAppVersionResponseAction(version);
    });

    window.electronApi.onRequestPermissionStatusResponse(async (status: { screen: boolean }) => {
        eventEmitter.emit('onRequestPermissionStatusResponse', { screen: status.screen });
    });

    // Event listeners
    eventEmitter.on('streamFinished', (payload: StreamFinishedPayload) => {
        const sessionId = sessionStore.streamSessionIdMap[payload.streamId];
        const promptConfigId = sessionStore.streamPromptConfigIdMap[payload.streamId];
        const variableChainHash = sessionStore.streamVariableChainHashMap[payload.streamId];
        if (!sessionId || !promptConfigId || !variableChainHash) {
            logger.error(
                'Failed to handle streamFinished event, missing mapping variables:',
                payload,
            );
            return;
        }

        VariableService.setPromptOutputVariable(
            sessionId,
            promptConfigId,
            variableChainHash,
            payload.fullContent,
        );

        void retrieveUserInfoAction();
    });

    // Request input sources
    requestInputSourcesAction();

    // Request desktop version string
    requestDesktopAppVersionAction();

    // Register a hook to listen for route changes to sync with web view
    registerRouterSyncHook();

    // Register subscribers to listen for store mutations to sync with web view
    registerClientStoreWatcher();
    registerSettingsStoreWatcher();
    registerPromptStoreWatcher();
    registerSourceStoreWatcher();

    // Start interval to fetch input source ID's every 3 seconds
    setInterval(() => {
        window.electronApi.requestSourcesIdList();
    }, 3000);

    // Start interval to refresh auth token
    setInterval(
        () => {
            void refreshTokenAction();
        },
        5 * 60 * 1000,
    ); // 5 minutes (token is valid for 15 minutes)

    // Verify WebSocket connection
    setInterval(() => {
        void WebSocketService.instance.verifyConnection();
    }, 2.5 * 1000); // 2.5 seconds

    void setTaskbarIconVisibilityAction(sessionStore.isTaskbarIconVisible, false);
    void registerHotkeysAction(JSON.parse(JSON.stringify(settingsStore.hotkeys)));

    document.body.classList.add('rounded');
    document.body.classList.add('bg-transparent');

    setAppSizeAction(640, 900);
};

export const bootstrapWebView = () => {
    const sessionSettingsStore = useSessionStore();
    sessionSettingsStore.isInsideWebView = true;

    // Start interval to refresh auth token
    setInterval(
        () => {
            void refreshTokenAction();
        },
        5 * 60 * 1000,
    ); // 5 minutes (token is valid for 15 minutes)

    // Verify WebSocket connection
    setInterval(() => {
        void WebSocketService.instance.verifyConnection();
    }, 2.5 * 1000); // 2.5 seconds
};

const runStoreMigrations = () => {
    /* region Migrate legacy settings store */
    const legacySettingsStore = localStorage.getItem('settings');

    let legacySettingsStoreJson;
    if (legacySettingsStore) {
        try {
            legacySettingsStoreJson = JSON.parse(legacySettingsStore);
        } catch (err) {
            logger.error('Failed to parse legacy settings store:', err);
        }

        if (!legacySettingsStoreJson) {
            return;
        }

        const settingsStore = useSettingsStore();

        if (legacySettingsStoreJson.rememberedUsername !== undefined) {
            settingsStore.rememberedUsername = legacySettingsStoreJson.rememberedUsername;
        }

        localStorage.removeItem('settings');
    }
    /* endregion Migrate legacy settings store */

    /* region Hotkey migration */
    const settingsStore = useSettingsStore();
    if (
        settingsStore.hotkeys['SOLVE'] !== undefined ||
        settingsStore.hotkeys['RESET'] !== undefined ||
        settingsStore.hotkeys['HIDE'] !== undefined ||
        settingsStore.hotkeys['CHANGE_LANGUAGE'] !== undefined ||
        settingsStore.hotkeys['QUIT'] !== undefined
    ) {
        createTextToast(
            'Hotkeys have been reset',
            'New hotkeys are now available. Due to conflicts with existing hotkeys, all hotkeys have been reset.',
            'success',
            true,
        );
        settingsStore.hotkeys = JSON.parse(JSON.stringify(DefaultHotkeys));
    }
    /* endregion Hotkey migration */

    /* region Move app hotkeys migration */
    if (settingsStore.hotkeys['MOVE_APP_UP'] === undefined) {
        let hotkeysCopy = JSON.parse(JSON.stringify(settingsStore.hotkeys));
        hotkeysCopy = {
            ...hotkeysCopy,
            MOVE_APP_UP: DefaultHotkeys['MOVE_APP_UP'],
            MOVE_APP_DOWN: DefaultHotkeys['MOVE_APP_DOWN'],
            MOVE_APP_LEFT: DefaultHotkeys['MOVE_APP_LEFT'],
            MOVE_APP_RIGHT: DefaultHotkeys['MOVE_APP_RIGHT'],
        };

        settingsStore.hotkeys = hotkeysCopy;
    }
    /* endregion Move app hotkeys migration */
};
